'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as Ariakit from '@ariakit/react';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
export function Tab(_props) {
    return null;
}
export function TabGroup({ children, onTabSelect, className }) {
    const [animate, setAnimate] = useState(false);
    const childArray = useMemo(() => React.Children.toArray(children).filter(Boolean), [children]);
    const defaultIndex = useMemo(() => {
        const defaultTabs = childArray.filter(child => child.props.default);
        const firstDefault = defaultTabs.findIndex(child => child.props.default);
        if (defaultTabs.length > 1 && process.env.NODE_ENV !== 'production') {
            console.warn('[TabGroup] Multiple <Tab default /> found. Using the first one at index');
        }
        return firstDefault;
    }, [childArray]);
    const tabItems = useMemo(() => {
        return childArray.map((child, i) => {
            const id = `tab-${i}`;
            const name = child.props.name ?? `Tab ${i + 1}`;
            const renderProp = child.props.render;
            const label = renderProp ?? name;
            return {
                id,
                name,
                label,
                panel: child.props.children,
            };
        });
    }, [childArray]);
    const defaultSelectedId = useMemo(() => (tabItems[defaultIndex] ? tabItems[defaultIndex].id : 'tab-0'), [tabItems, defaultIndex]);
    const store = Ariakit.useTabStore({ defaultSelectedId });
    const [indicator, setIndicator] = useState({ offsetX: 0, width: 0 });
    const listRef = useRef(null);
    const updateIndicator = useCallback(() => {
        const list = listRef.current;
        if (!list)
            return;
        const selected = list.querySelector('[role="tab"][aria-selected="true"]');
        if (selected)
            setIndicator({ offsetX: selected.offsetLeft, width: selected.offsetWidth });
    }, []);
    const selectedId = Ariakit.useStoreState(store, 'selectedId');
    useLayoutEffect(() => {
        updateIndicator();
        const id = requestAnimationFrame(() => setAnimate(true));
        return () => cancelAnimationFrame(id);
    }, [updateIndicator, selectedId]);
    useEffect(() => {
        if (!selectedId)
            return;
        const current = tabItems.find(i => i.id === selectedId);
        if (current && onTabSelect)
            onTabSelect(current.name);
    }, [selectedId, tabItems, onTabSelect, updateIndicator]);
    useEffect(() => {
        const onResize = updateIndicator;
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [updateIndicator]);
    return (_jsx(Group, { className: className, children: _jsxs(Ariakit.TabProvider, { store: store, children: [_jsxs(ListWrap, { children: [_jsx(TabList, { ref: listRef, children: tabItems.map(({ id, label }) => (_jsx(TabButton, { id: id, render: _jsx("button", {}), "$selected": selectedId === id, "aria-controls": `panel-${id}`, children: label }, id))) }), _jsx(Indicator, { "$offsetX": indicator.offsetX, "$width": indicator.width, "$animate": animate })] }), tabItems.map(({ id, panel }) => (_jsx(Panel, { tabId: id, id: `panel-${id}`, children: panel }, `panel-${id}`)))] }) }));
}
const Group = styled.div `
  width: 100%;
  padding: ${o => o.theme.spacing(4)};
`;
const ListWrap = styled.div `
  position: relative;
  border-bottom: 1px solid ${o => o.theme.color.stroke.neutral.border.decoration.on.background};
`;
const TabList = styled(Ariakit.TabList) `
  display: flex;
  gap: ${o => o.theme.spacing(6)};
`;
const TabButton = styled(Ariakit.Tab) `
  position: relative;
  padding: ${o => o.theme.spacing(3)} 0;
  margin: 0;
  font: inherit;
  color: ${o => o.$selected
    ? o.theme.color.text.primary.on.background
    : o.theme.color.stroke.disabled.text.on.background};
  transition: color 200ms ease;
  font-weight: 700;
  text-transform: uppercase;

  &:hover {
    color: ${o => o.theme.color.text.primary.on.background};
  }
`;
const Indicator = styled.div `
  position: absolute;
  bottom: -1px;
  height: 4px;
  background: ${o => o.theme.color.fill.strong};
  width: ${p => p.$width}px;
  transform: translateX(${p => p.$offsetX}px);
  transition: ${p => (p.$animate ? 'transform 250ms ease, width 250ms ease' : 'none')};
  will-change: transform, width;
`;
const Panel = styled(Ariakit.TabPanel) `
  padding: ${o => o.theme.spacing(4)} 0;
`;
//# sourceMappingURL=TabGroup.js.map